import {
    take, all, call, fork, put, race, takeLatest, select, spawn, cancel
} from 'redux-saga/effects';
import { fromJS } from 'immutable';
import Big from 'big.js';
import { RETRIEVED_BRAND_CONFIG } from 'capi/redux';

import { logError } from '../utils/errorUtils';
import { formatAmountToString } from '../utils/numberUtils';
import { getDefaultCurrencyCode } from '../app/bootstrap';
import {
    IS_PLASTIC_FIELD,
    CURRENCY_FIELD,
    AMOUNT_FIELD,
    PROGRAM_CODE,
    newItemIsPlastic,
    newItemCurrency, FACEPLATE_FIELD
} from '../item/newItemForm';
import { SET_CURRENCY, setCurrency, updateIntlCurrency } from '../intl/intlModule';
import {
    formats, getIntl,
    getIntlCurrencyCode,
    getIntlLocale
} from '../intl/intlSelectors';
import {
    setDefaultFieldValues,
    setInitialValues,
    changeValue,
    fieldChangedAction
} from '../item/newItemFormModule';
import { getPaidItemsCount, getCurrencyCodes } from '../item/itemSelectors';
import { FACEPLATES_LOADED } from '../faceplate/faceplateModule';
import { PROGRAM_LOADED } from '../program/programModule';

import { CATALOGS_LOADED } from '../brand/brandModule';
import {
    getCatalogs,
    getElectronicCurrencyList,
    getPlasticCurrencyList,
    getValidDefaultAmountForCatalogs
} from './catalogSelectors';
import { getActiveProgramCode } from '../program/programSelectors';
import { predictIntlCurrency } from '../intl/data/currencyByLocale';
import { updateCurrency } from '../intl/intlSagas';
import { getCartCurrencyCode } from '../payment/paymentSelectors';
import { getPreferredAmount, getPreferredCardType, getPreferredFaceplateCode } from '../routing/flowSelectors';
import { isFaceplateInPreferredGroup } from '../faceplate/faceplateSelectors';
import { removeFieldFromFlowProgress, removePreferredValue } from '../routing/flowProgressModule';

const IS_PLASTIC_FIELD_CHANGED = fieldChangedAction(IS_PLASTIC_FIELD);
const CURRENCY_FIELD_CHANGED = fieldChangedAction(CURRENCY_FIELD);
const AMOUNT_FIELD_CHANGED = fieldChangedAction(AMOUNT_FIELD);



export const getCurrencyDefault = (
    isPlastic, electronicCurrencyList, plasticCurrencyList, defaultCurrency = null, intlCurrency = null
) => {
    const selectedCurrencyList = isPlastic ? plasticCurrencyList : electronicCurrencyList;
    if (defaultCurrency && selectedCurrencyList.contains(defaultCurrency)) {
        return defaultCurrency;
    } if (intlCurrency && selectedCurrencyList.contains(intlCurrency)) {
        
        
        return intlCurrency;
    }
    return selectedCurrencyList.first();
};


export const getAmountDefault = (isPlastic, currency, catalogs, preferredAmount) => {
    const defaultAmountSelector = isPlastic ? 'plasticDefaultAmount' : 'egcDefaultAmount';
    const fixedCatalogSelector = isPlastic ? 'plasticFixedCatalog' : 'egcFixedCatalog';
    const rangedCatalogSelector = isPlastic ? 'plasticRangedCatalog' : 'egcRangedCatalog';
    const defaultAmount = new Big(preferredAmount || catalogs.getIn([currency, defaultAmountSelector]));
    const fixedCatalog = catalogs.getIn([currency, fixedCatalogSelector]);
    const rangedCatalog = catalogs.getIn([currency, rangedCatalogSelector]);

    if (!fixedCatalog && !rangedCatalog) {
        logError(new Error(`No fixed or ranged catalog found for currency: ${currency}`));
        return new Big('0');
    }
    return new Big(getValidDefaultAmountForCatalogs(fixedCatalog, rangedCatalog, defaultAmount).toFixed(2));
};


function* resetDefaultCurrency(updatedIsPlastic) {
    const isCartEmpty = !(yield select(getPaidItemsCount));
    const currentCurrency = yield select(newItemCurrency);
    let updatedCurrency = currentCurrency;

    
    
    if (isCartEmpty) {
        const electronicCurrencyList = yield select(getElectronicCurrencyList);
        const plasticCurrencyList = yield select(getPlasticCurrencyList);
        const selectedCurrencyList = updatedIsPlastic ? plasticCurrencyList : electronicCurrencyList;

        
        if (!selectedCurrencyList.contains(currentCurrency)) {
            updatedCurrency = yield call(getCurrencyDefault,
                updatedIsPlastic, electronicCurrencyList, plasticCurrencyList);
        }
    } else {
        
        
        
        
        const cartCurrency = yield select(getCartCurrencyCode);
        if (currentCurrency !== cartCurrency) {
            updatedCurrency = cartCurrency;
        }
    }

    if (updatedCurrency !== currentCurrency) {
        yield put(setCurrency(updatedCurrency));
    } else {
        
        yield call(updateCurrencyAndAmount, currentCurrency, updatedIsPlastic);
    }
}


function* updateCurrencyAndAmount(updatedCurrency, updatedIsPlastic) {
    const parsedCatalogs = yield select(getCatalogs);
    const intlFormats = yield select(formats);
    const isPlastic = updatedIsPlastic || (yield select(newItemIsPlastic));
    const preferredAmount = yield select(getPreferredAmount);

    
    const updatedAmount = formatAmountToString(
        new Big(
            yield call(getAmountDefault, isPlastic, updatedCurrency, parsedCatalogs, preferredAmount)
        )
    );
    
    yield put(updateIntlCurrency(intlFormats, updatedCurrency));

    
    const updatedValues = fromJS({
        [CURRENCY_FIELD]: updatedCurrency,
        [AMOUNT_FIELD]: updatedAmount
    });
    yield put(setInitialValues(updatedValues));

    
    yield put(changeValue(CURRENCY_FIELD, updatedCurrency));
    yield put(changeValue(AMOUNT_FIELD, updatedAmount));
}


export function* handleSetDefaults() {
    const catalogs = yield select(getCatalogs);
    const isPlastic = yield select(newItemIsPlastic);
    const intlFormats = yield select(formats);
    const intlCurrencyCode = yield select(getIntlCurrencyCode);
    const defaultCurrencyCode = yield call(getDefaultCurrencyCode);
    const cartCurrency = (yield select(getCurrencyCodes)).first();
    const electronicCurrencyList = yield select(getElectronicCurrencyList);
    const plasticCurrencyList = yield select(getPlasticCurrencyList);
    const activeProgramCode = yield select(getActiveProgramCode);

    
    const defaultCurrency = cartCurrency || (yield call(getCurrencyDefault,
        isPlastic,
        electronicCurrencyList,
        plasticCurrencyList,
        defaultCurrencyCode,
        intlCurrencyCode));
    
    yield put(updateIntlCurrency(intlFormats, defaultCurrency));

    const preferredAmount = yield select(getPreferredAmount);
    
    const defaultAmount = formatAmountToString(new Big(yield call(getAmountDefault, isPlastic, defaultCurrency, catalogs, preferredAmount)));

    
    let defaultValues = fromJS({
        [CURRENCY_FIELD]: defaultCurrency,
        [AMOUNT_FIELD]: defaultAmount,
        [IS_PLASTIC_FIELD]: isPlastic
    });
    if (activeProgramCode) {
        defaultValues = defaultValues.set(PROGRAM_CODE, activeProgramCode);
    }

    yield put(setDefaultFieldValues(defaultValues));

    yield put({ type: CATALOGS_LOADED });
}

export function* validatePreferredFaceplateCode(faceplateCode, preferredIsPlastic = null) {
    const isPlastic = preferredIsPlastic || (yield select(newItemIsPlastic));
    const matchesDelivery = (yield select(isFaceplateInPreferredGroup))(isPlastic, faceplateCode);
    if (!matchesDelivery) {
        yield put(removeFieldFromFlowProgress(FACEPLATE_FIELD));
        yield put(removePreferredValue(FACEPLATE_FIELD));
    }
}


export function* watchIsPlasticAndCurrencyChange() {
    
    yield take(CATALOGS_LOADED);

    
    yield takeLatest(SET_CURRENCY, function* handleUpdate(action) {
        const currentCurrency = yield select(newItemCurrency);
        const newCurrency = action.currency;
        if (currentCurrency !== newCurrency) {
            yield fork(updateCurrencyAndAmount, newCurrency);
        }
    });

    
    yield spawn(function* watchIsPlasticChange() {
        while (true) {
            
            const action = yield take(IS_PLASTIC_FIELD_CHANGED);
            const preferredFaceplateCode = yield select(getPreferredFaceplateCode);
            if (preferredFaceplateCode) {
                yield call(validatePreferredFaceplateCode, preferredFaceplateCode);
            }
            const handleIsPlasticUpdateTask = yield fork(resetDefaultCurrency, action.newValue);

            
            
            
            
            
            const { currencyChanged } = yield race({
                currencyChanged: take(CURRENCY_FIELD_CHANGED),
                amountChanged: take(AMOUNT_FIELD_CHANGED)
            });
            if (currencyChanged) {
                yield cancel(handleIsPlasticUpdateTask);
            }
        }
    });
}

export function* watchCatalogsReceived() {
    
    
    const actionsToAwait = {
        catalog: take(RETRIEVED_BRAND_CONFIG.CATALOG),
        faceplates: take(FACEPLATES_LOADED)
    };

    const programCode = yield select(getActiveProgramCode);
    if (programCode) {
        
        actionsToAwait.programs = take(PROGRAM_LOADED);
    }
    yield all(actionsToAwait);
    const intl = yield select(getIntl);
    const hasStoredIntlData = Boolean(intl && intl.formats);
    
    if (!hasStoredIntlData) {
        const locale = yield select(getIntlLocale);
        if (locale) {
            const currency = predictIntlCurrency(locale);
            yield call(updateCurrency, currency);
        } else {
            yield call(updateCurrency, 'USD');
        }
    }
    const preferredFaceplateCode = yield select(getPreferredFaceplateCode);
    if (preferredFaceplateCode) {
        const preferredCardType = yield select(getPreferredCardType);
        yield call(validatePreferredFaceplateCode, preferredFaceplateCode, preferredCardType);
    }
    yield call(handleSetDefaults);
}
